import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/apiClient";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ExternalLink, RotateCw, Trash2, Loader2, GitBranch, Plus, X, Copy,
  Globe, ShieldCheck, CheckCircle2, Circle, Terminal, Server, Zap,
} from "lucide-react";
import { toast } from "sonner";

function LogsTab({ id }) {
  const [data, setData] = useState(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const boxRef = useRef(null);

  const fetchLogs = useCallback(() => {
    api.get(`/projects/${id}/logs`).then((r) => setData(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    fetchLogs();
    const t = setInterval(fetchLogs, 5000);
    return () => clearInterval(t);
  }, [fetchLogs]);

  useEffect(() => {
    if (autoScroll && boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [data, autoScroll]);

  const copy = () => {
    const text = (data?.lines || []).map((l) => l.text).join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Logs copied");
  };

  const color = (lvl) => (lvl === "error" ? "text-red-400" : lvl === "success" ? "text-emerald-400" : "text-neutral-400");

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm text-neutral-400">
          <Terminal className="h-4 w-4" /> Build output
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-neutral-400 cursor-pointer">
            <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} data-testid="autoscroll-toggle" />
            Auto-scroll
          </label>
          <button onClick={copy} data-testid="copy-logs-button" className="inline-flex items-center gap-1.5 rounded-md border border-[#262626] px-2.5 py-1 text-xs text-neutral-300 hover:bg-[#1F1F1F] transition-colors duration-200">
            <Copy className="h-3 w-3" /> Copy
          </button>
        </div>
      </div>
      <div ref={boxRef} data-testid="logs-terminal" className="terminal-scroll bg-black border border-[#262626] rounded-lg p-4 font-mono text-xs h-[360px] overflow-y-auto">
        {!data && <div className="text-neutral-600">Connecting…</div>}
        {data && data.lines.length === 0 && <div className="text-neutral-600">No build output yet. Trigger a deploy.</div>}
        {data?.lines.map((l, i) => (
          <div key={i} className={`${color(l.level)} whitespace-pre-wrap leading-relaxed`}>
            <span className="text-neutral-700 select-none mr-2">$</span>{l.text}
          </div>
        ))}
      </div>

      {data?.deploys?.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm text-neutral-400 mb-3">Deploy history</h4>
          <div className="rounded-lg border border-[#262626] overflow-hidden">
            {data.deploys.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-4 py-2.5 border-b border-[#1a1a1a] text-xs">
                <span className="font-mono text-neutral-300">{d.commit_ref || "—"} · {d.branch}</span>
                <StatusBadge status={d.state === "ready" || d.state === "current" ? "Live" : (d.state === "error" ? "Failed" : "Building")} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EnvTab({ id }) {
  const [vars, setVars] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/projects/${id}/env`).then((r) => setVars(r.data.env_vars.length ? r.data.env_vars : [])).catch(() => {});
  }, [id]);

  const update = (i, field, val) => setVars((v) => v.map((row, idx) => (idx === i ? { ...row, [field]: val } : row)));
  const add = () => setVars((v) => [...v, { key: "", value: "" }]);
  const remove = (i) => setVars((v) => v.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const clean = vars.filter((v) => v.key.trim());
      const r = await api.post(`/projects/${id}/env`, { env_vars: clean });
      setVars(r.data.env_vars);
      toast.success("Environment variables pushed to Netlify");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-sm text-neutral-400 mb-4">Variables are pushed to Netlify. Trigger a redeploy for them to take effect.</p>
      <div className="space-y-2 mb-4">
        {vars.length === 0 && <p className="text-sm text-neutral-600" data-testid="env-empty">No variables yet.</p>}
        {vars.map((v, i) => (
          <div key={i} className="flex gap-2" data-testid={`env-row-${i}`}>
            <input value={v.key} onChange={(e) => update(i, "key", e.target.value)} placeholder="KEY" data-testid={`env-key-${i}`}
              className="flex-1 h-10 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 text-sm font-mono outline-none focus:ring-2 focus:ring-neutral-500" />
            <input value={v.value} onChange={(e) => update(i, "value", e.target.value)} placeholder="value" data-testid={`env-value-${i}`}
              className="flex-1 h-10 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 text-sm font-mono outline-none focus:ring-2 focus:ring-neutral-500" />
            <button onClick={() => remove(i)} data-testid={`env-remove-${i}`} className="h-10 w-10 flex items-center justify-center rounded-md border border-[#262626] text-neutral-400 hover:bg-[#1F1F1F] transition-colors duration-200">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={add} data-testid="env-add-button" className="inline-flex items-center gap-1.5 rounded-md border border-[#262626] px-3 py-2 text-sm text-neutral-300 hover:bg-[#1F1F1F] transition-colors duration-200">
          <Plus className="h-4 w-4" /> Add variable
        </button>
        <button onClick={save} disabled={saving} data-testid="env-save-button" className="inline-flex items-center gap-1.5 rounded-md bg-white text-black px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors duration-200 disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & push"}
        </button>
      </div>
    </div>
  );
}

function DomainTab({ project, reload }) {
  const id = project.id;
  const [domain, setDomain] = useState(project.custom_domain || "");
  const [busy, setBusy] = useState("");
  const [verify, setVerify] = useState(null);
  const [ncDomains, setNcDomains] = useState(null); // null=loading, []=none/not connected

  useEffect(() => {
    api.get("/namecom/domains").then((r) => setNcDomains(r.data)).catch(() => setNcDomains([]));
  }, []);

  const addDomain = async () => {
    setBusy("domain");
    try {
      await api.post(`/projects/${id}/domain`, { domain });
      toast.success("Custom domain set on Netlify");
      await reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const autoDns = async () => {
    setBusy("dns");
    try {
      const r = await api.post(`/projects/${id}/dns`);
      toast.success("DNS records created on Name.com");
      setVerify(null);
      console.log(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const runVerify = async () => {
    setBusy("verify");
    try {
      const r = await api.get(`/projects/${id}/dns/verify`);
      setVerify(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const removeDomain = async () => {
    if (!window.confirm(`Remove ${project.custom_domain} from this project? (You can add a different domain afterwards.)`)) return;
    setBusy("remove");
    try {
      await api.delete(`/projects/${id}/domain`);
      toast.success("Domain removed");
      setDomain("");
      setVerify(null);
      await reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const base = (project.custom_domain || "").replace(/^www\./, "");
  const wwwTarget = `${project.site_name}.netlify.app`;

  const Check = ({ ok, children }) => (
    <div className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-neutral-600" />}
      <span className={ok ? "text-neutral-200" : "text-neutral-400"}>{children}</span>
    </div>
  );

  return (
    <div className="max-w-xl space-y-6">
      {ncDomains && ncDomains.length > 0 && (
        <div className="rounded-lg border border-[#262626] bg-[#141414] p-4">
          <label className="block text-xs text-neutral-400 mb-1.5 flex items-center gap-1.5"><Server className="h-3.5 w-3.5" /> Pick from your Name.com domains</label>
          <select
            data-testid="namecom-domain-select"
            value=""
            onChange={(e) => e.target.value && setDomain(e.target.value)}
            className="w-full h-10 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 text-sm outline-none focus:ring-2 focus:ring-neutral-500"
          >
            <option value="">Select a domain…</option>
            {ncDomains.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      )}
      {ncDomains && ncDomains.length === 0 && (
        <p className="text-xs text-neutral-500">
          Connect Name.com on the <Link to="/connections" className="underline">Connections</Link> page to pick a domain and auto-configure DNS. You can still enter any domain manually below.
        </p>
      )}

      <div>
        <label className="block text-sm text-neutral-300 mb-1.5 flex items-center gap-1.5"><Globe className="h-4 w-4" /> Custom domain</label>
        <div className="flex gap-2">
          <input value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="domain-input" placeholder="example.com"
            className="flex-1 h-10 rounded-md border border-[#262626] bg-[#0A0A0A] px-3 text-sm font-mono outline-none focus:ring-2 focus:ring-neutral-500" />
          <button onClick={addDomain} disabled={busy === "domain" || domain.length < 3} data-testid="add-domain-button"
            className="rounded-md bg-white text-black px-4 text-sm font-medium hover:bg-neutral-200 transition-colors duration-200 disabled:opacity-50">
            {busy === "domain" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set domain"}
          </button>
        </div>
      </div>

      {project.custom_domain && (
        <>
          <div className="rounded-lg border border-[#262626] bg-[#141414] p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium">Current domain: <span className="font-mono text-neutral-300">{project.custom_domain}</span></h4>
              <button onClick={removeDomain} disabled={busy === "remove"} data-testid="remove-domain-button"
                className="inline-flex items-center gap-1.5 rounded-md border border-[#262626] px-2.5 py-1 text-xs text-red-400 hover:bg-[#1F1F1F] transition-colors duration-200 disabled:opacity-50">
                {busy === "remove" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Remove domain
              </button>
            </div>
            <div className="text-xs text-neutral-500 mb-3">Required DNS records:</div>
            <div className="space-y-2 font-mono text-xs">
              <div className="flex justify-between border-b border-[#1a1a1a] pb-2"><span className="text-neutral-500">A</span><span className="text-neutral-300">@</span><span className="text-neutral-300">75.2.60.5</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">CNAME</span><span className="text-neutral-300">www</span><span className="text-neutral-300">{wwwTarget}</span></div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={autoDns} disabled={busy === "dns"} data-testid="auto-dns-button"
              className="inline-flex items-center gap-1.5 rounded-md bg-white text-black px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors duration-200 disabled:opacity-50">
              {busy === "dns" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Server className="h-4 w-4" />} Auto-configure via Name.com
            </button>
            <button onClick={runVerify} disabled={busy === "verify"} data-testid="verify-dns-button"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#262626] px-4 py-2 text-sm text-neutral-300 hover:bg-[#1F1F1F] transition-colors duration-200 disabled:opacity-50">
              {busy === "verify" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Verify propagation
            </button>
          </div>

          <p className="text-xs text-neutral-600 leading-relaxed">
            Using another registrar? Add the two records above manually in your DNS provider, then verify.
          </p>

          {verify && (
            <div className="rounded-lg border border-[#262626] bg-[#141414] p-5 space-y-3" data-testid="verify-results">
              <Check ok={verify.apex.ok}>Apex A → {base} points to {verify.apex.expected} {verify.apex.observed?.length ? `(saw ${verify.apex.observed.join(", ")})` : "(not resolved yet)"}</Check>
              <Check ok={verify.www.ok}>www CNAME → {verify.www.expected} {verify.www.observed?.length ? "" : "(not resolved yet)"}</Check>
              {verify.using_namecom_dns === false && (
                <p className="text-xs text-red-400 leading-relaxed">
                  This domain's nameservers are {verify.nameservers?.join(", ") || "external"} — not Name.com. Name.com can't manage its DNS until you switch the nameservers to Name.com (ns1–ns4.name.com), or add the A/CNAME records above at your current DNS host.
                </p>
              )}
              <Check ok={verify.https}>HTTPS certificate active</Check>
              <Check ok={verify.site_ok}>
                Site serving content{verify.site_status ? ` (Netlify returned HTTP ${verify.site_status})` : ""}
              </Check>
              {verify.https && !verify.site_ok && (
                <p className="text-xs text-amber-400/90 leading-relaxed pt-1">
                  DNS &amp; HTTPS reach Netlify, but Netlify returned {verify.site_status || "an error"} — there is no published production deploy for this site yet. Open the <span className="font-medium">Build Logs</span> tab: if the last deploy Failed, fix the build command / publish directory and Redeploy. The domain will serve once a deploy is Live.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// small local Server icon import fix

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch((e) => { toast.error(apiError(e)); }), [id]);
  useEffect(() => { load(); }, [load]);

  // Keep refreshing while a deploy is in progress so status/URL persist until done.
  useEffect(() => {
    if (project?.status !== "Building") return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [project?.status, load]);

  const redeploy = async () => {
    setBusy("deploy");
    try {
      await api.post(`/projects/${id}/deploy`);
      toast.success("Deploy triggered");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const del = async () => {
    if (!window.confirm("Remove this project from DeployBridge? (The Netlify site is not deleted.)")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project removed");
      navigate("/");
    } catch (e) { toast.error(apiError(e)); }
  };

  const enableAuto = async () => {
    setBusy("auto");
    try {
      await api.post(`/projects/${id}/auto-deploy`);
      toast.success("Auto-deploy enabled — pushes to this branch now rebuild automatically");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!project) {
    return <Layout><div className="h-64 flex items-center justify-center text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div></Layout>;
  }

  return (
    <Layout>
      <Link to="/" className="text-xs text-neutral-500 hover:text-white transition-colors duration-200">← Projects</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mt-3 mb-8">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tighter truncate">{project.name}</h1>
            <StatusBadge status={project.status} />
          </div>
          <p className="text-sm text-neutral-500 font-mono mt-2 flex items-center gap-1.5">
            <GitBranch className="h-3.5 w-3.5" /> {project.repo_full_name} · {project.branch}
          </p>
          {project.site_url && (
            <a href={project.site_url} target="_blank" rel="noreferrer" data-testid="live-url"
              className="text-sm text-neutral-300 hover:text-white transition-colors duration-200 inline-flex items-center gap-1.5 mt-2">
              {project.site_url.replace("https://", "")} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          <div className="mt-3">
            {project.auto_deploy ? (
              <span data-testid="auto-deploy-on" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-400">
                <Zap className="h-3.5 w-3.5" /> Auto-deploys on push to {project.branch}
              </span>
            ) : (
              <button onClick={enableAuto} disabled={busy === "auto"} data-testid="enable-auto-deploy-button"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#262626] px-2.5 py-1 text-xs font-medium text-neutral-300 hover:bg-[#1F1F1F] transition-colors duration-200 disabled:opacity-50">
                {busy === "auto" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                Enable auto-deploy on push
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={redeploy} disabled={busy === "deploy"} data-testid="redeploy-detail-button"
            className="inline-flex items-center gap-1.5 rounded-md bg-white text-black px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors duration-200 disabled:opacity-50">
            {busy === "deploy" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />} Redeploy
          </button>
          <button onClick={del} data-testid="delete-project-button"
            className="inline-flex items-center justify-center rounded-md border border-[#262626] px-3 py-2 text-red-400 hover:bg-[#1F1F1F] transition-colors duration-200">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <Tabs defaultValue="logs">
        <TabsList className="bg-[#141414] border border-[#262626]">
          <TabsTrigger value="logs" data-testid="tab-logs">Build Logs</TabsTrigger>
          <TabsTrigger value="env" data-testid="tab-env">Environment</TabsTrigger>
          <TabsTrigger value="domain" data-testid="tab-domain">Domain & DNS</TabsTrigger>
        </TabsList>
        <TabsContent value="logs" className="mt-6"><LogsTab id={id} /></TabsContent>
        <TabsContent value="env" className="mt-6"><EnvTab id={id} /></TabsContent>
        <TabsContent value="domain" className="mt-6"><DomainTab project={project} reload={load} /></TabsContent>
      </Tabs>
    </Layout>
  );
}
